import { PROCEDURES_ALPHABETICAL, procedureByKey, searchProcedures } from '@iculogbook/shared';
import type { ProcedureDef } from '@iculogbook/shared';
import { useMemo, useState } from 'react';

import { TextInput } from '../../components/index.ts';
import { SearchIcon } from '../../layout/icons.tsx';
import { cx } from '../../lib/cx.ts';

type ProcedurePickerProps = {
  onSelect: (procedureKey: string) => void;
  /** Most recent first. Pinned above the divider when the search box is empty. */
  recentKeys: readonly string[];
  /** The key already chosen, marked in the list when the picker is reopened. */
  selectedKey?: string | undefined;
  autoFocus?: boolean;
};

/**
 * The searchable procedure list — the first thing the quick-add screen shows,
 * and the control the ten-second target lives or dies by.
 *
 * Alphabetical when the box is empty, with this trainee's five most recent
 * procedures pinned above a divider; typing filters on label and synonyms.
 * Selecting advances immediately: there is no "next" button, because a tap that
 * only confirms a tap is a tap wasted.
 *
 * Also used by the case form in a bottom sheet, so it renders no chrome of its
 * own beyond the search box and the list.
 */
export function ProcedurePicker({
  onSelect,
  recentKeys,
  selectedKey,
  autoFocus = false,
}: ProcedurePickerProps) {
  const [query, setQuery] = useState('');
  const searching = query.trim() !== '';

  const results = useMemo(() => searchProcedures(query), [query]);

  const recent = useMemo(
    () =>
      recentKeys
        .map((key) => procedureByKey(key))
        .filter((definition): definition is ProcedureDef => definition !== undefined),
    [recentKeys],
  );

  const recentKeySet = new Set(recent.map((definition) => definition.key));

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-faint" />
        <TextInput
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            // Enter takes the top result. Typing "art" then Enter is the
            // fastest path there is, and it costs one line.
            if (event.key !== 'Enter') return;
            event.preventDefault();
            const first = results[0];
            if (first !== undefined) onSelect(first.key);
          }}
          placeholder="Search procedures"
          aria-label="Search procedures"
          autoFocus={autoFocus}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          className="pl-10"
        />
      </div>

      {searching ? (
        results.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-muted">
            Nothing matches “{query.trim()}”. Try a shorter word, or an abbreviation such as CVC.
          </p>
        ) : (
          <ProcedureList
            procedures={results}
            selectedKey={selectedKey}
            onSelect={onSelect}
            label={`${String(results.length)} matching ${results.length === 1 ? 'procedure' : 'procedures'}`}
          />
        )
      ) : (
        <>
          {recent.length === 0 ? null : (
            <ProcedureList
              procedures={recent}
              selectedKey={selectedKey}
              onSelect={onSelect}
              label="Recently used"
            />
          )}

          <ProcedureList
            procedures={PROCEDURES_ALPHABETICAL}
            selectedKey={selectedKey}
            onSelect={onSelect}
            label="All procedures"
            dimmedKeys={recentKeySet}
          />
        </>
      )}
    </div>
  );
}

function ProcedureList({
  procedures,
  label,
  selectedKey,
  onSelect,
  dimmedKeys,
}: {
  procedures: readonly ProcedureDef[];
  label: string;
  selectedKey: string | undefined;
  onSelect: (procedureKey: string) => void;
  /** Already pinned above the divider; shown here quietly rather than removed. */
  dimmedKeys?: ReadonlySet<string>;
}) {
  return (
    <section className="flex flex-col gap-1">
      <h2 className="px-1 pt-2 text-xs font-medium tracking-wide text-faint uppercase">{label}</h2>

      <ul className="flex flex-col">
        {procedures.map((procedure) => {
          const selected = procedure.key === selectedKey;

          return (
            <li key={procedure.key}>
              <button
                type="button"
                onClick={() => onSelect(procedure.key)}
                aria-current={selected ? 'true' : undefined}
                className={cx(
                  'flex min-h-touch w-full items-center rounded-md px-3 py-2 text-left text-base',
                  'transition-colors duration-(--ui-duration-fast) hover:bg-surface-raised',
                  selected ? 'bg-accent-quiet text-accent' : 'text-text',
                  dimmedKeys?.has(procedure.key) === true && !selected ? 'text-muted' : '',
                )}
              >
                {procedure.label}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
